import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AnimatedModal from '../../components/ui/AnimatedModal';
import { resolveCandidateDisplayName } from './screeningConstants';

export default function InsightsTab(props) {
  const insightsStats = props.insightsStats;
  const invitedCount = props.invitedCount;
  const completedCount = props.completedCount;
  const shortlistedCount = props.shortlistedCount;
  const insightsQuestionsCount = props.insightsQuestionsCount;
  const insightsModalCard = props.insightsModalCard;
  const setInsightsModalCard = props.setInsightsModalCard;
  const setInsightsModalQuestionsSubTab = props.setInsightsModalQuestionsSubTab;
  const insightsModalQuestionsSubTab = props.insightsModalQuestionsSubTab;
  const insightsModalAssessmentQuestions = props.insightsModalAssessmentQuestions;
  const insightsModalInterviewQuestions = props.insightsModalInterviewQuestions;
  const insightsModalQuestionsLoading = props.insightsModalQuestionsLoading;
  const candidates = props.candidates;
  const screeningMeta = props.screeningMeta;
  const candidatePoolInsights = props.candidatePoolInsights;
  const getOverallScoreNumber = props.getOverallScoreNumber;
  const insightsReevaluateLoading = props.insightsReevaluateLoading;
  const screeningId = props.screeningId;
  const refreshScreeningInsights = props.refreshScreeningInsights;

  const s = insightsStats;

const kpiCards = [

  {

    key: 'applied',

    title: 'Candidates Applied',

    value: invitedCount,

    icon: HeroIcons.UsersIcon,

    color: 'text-[#007BFF]',

    bg: 'bg-[#007BFF]/10',

  },

  {

    key: 'completed',

    title: 'Completed Full Process',

    value: completedCount,

    icon: HeroIcons.CheckCircleIcon,

    color: 'text-emerald-600',

    bg: 'bg-emerald-50',

  },

  {

    key: 'shortlisted',

    title: 'Selected',

    value: shortlistedCount,

    icon: HeroIcons.StarIcon,

    color: 'text-amber-600',

    bg: 'bg-amber-50',

  },

  {

    key: 'questions',

    title: 'Questions',

    value:

      insightsQuestionsCount != null

        ? insightsQuestionsCount.assessment + insightsQuestionsCount.interview

        : '—',

    icon: HeroIcons.AcademicCapIcon,

    color: 'text-slate-600',

    bg: 'bg-slate-100',

  },

  {

    key: 'avgResume',

    title: 'Avg Resume Score',

    value: s.avgResume != null ? `${s.avgResume}%` : '—',

    icon: HeroIcons.DocumentTextIcon,

    color: 'text-blue-600',

    bg: 'bg-blue-50',

  },

  {

    key: 'avgOverall',

    title: 'Avg Overall Score',

    value: s.avgOverall != null ? `${s.avgOverall}%` : '—',

    icon: HeroIcons.ChartBarIcon,

    color: 'text-[#007BFF]',

    bg: 'bg-[#007BFF]/10',

  },

  {

    key: 'avgAssessment',

    title: 'Avg Assessment Score',

    value: s.avgAssessment != null ? `${s.avgAssessment}%` : '—',

    icon: HeroIcons.ClipboardDocumentCheckIcon,

    color: 'text-violet-600',

    bg: 'bg-violet-50',

  },

  {

    key: 'avgInterview',

    title: 'Avg AI Interview Score',

    value: s.avgInterview != null ? `${s.avgInterview}%` : '—',

    icon: HeroIcons.ChatBubbleLeftRightIcon,

    color: 'text-indigo-600',

    bg: 'bg-indigo-50',

  },

];



const getInsightsModalCandidates = () => {

  if (!insightsModalCard) return [];

  switch (insightsModalCard) {

    case 'applied':

      return candidates;

    case 'completed':

      return candidates.filter((c) => c.status === 'Completed');

    case 'shortlisted':

      return candidates.filter((c) => c.shortlisted);

    case 'questions':

      return [];

    case 'avgResume':

      return candidates

        .filter((c) => c.resumeScore != null)

        .sort((a, b) => (b.resumeScore ?? 0) - (a.resumeScore ?? 0));

    case 'avgOverall':

      return candidates

        .filter((c) => getOverallScoreNumber(c) != null)

        .sort((a, b) => (getOverallScoreNumber(b) ?? 0) - (getOverallScoreNumber(a) ?? 0));

    case 'avgAssessment':

      return candidates

        .filter((c) => c.assessmentScore != null)

        .sort((a, b) => (b.assessmentScore ?? 0) - (a.assessmentScore ?? 0));

    case 'avgInterview':

      return candidates

        .filter((c) => c.interviewScore != null)

        .sort((a, b) => (b.interviewScore ?? 0) - (a.interviewScore ?? 0));

    default:

      return [];

  }

};



const modalCandidates = getInsightsModalCandidates();

const modalConfig = kpiCards.find((k) => k.key === insightsModalCard);



const renderKpiCard = (k) => {

  const displayValue = typeof k.value === 'number' ? k.value.toLocaleString() : k.value;

  return (

    <button

      key={k.title}

      type="button"

      onClick={() => setInsightsModalCard(k.key)}

      title={`${k.title}: ${displayValue}`}

      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-[#007BFF]/30 transition-all overflow-hidden min-w-0 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40 focus:ring-offset-1"

    >

      <div className="flex items-start gap-3">

        <div

          className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${k.bg} ${k.color}`}

        >

          <k.icon className="w-4 h-4" />

        </div>

        <div className="min-w-0 flex-1">

          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 truncate">

            {k.title}

          </p>

          <p className="mt-1 text-xl font-bold text-gray-900 truncate">{displayValue}</p>

        </div>

      </div>

    </button>

  );

};



return (

  <div className="space-y-10">

    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

      <p className="text-sm text-gray-500 max-w-2xl">

        Insights are computed from this screening&apos;s candidate records. If numbers look stale after scoring or imports, refresh to bypass cache and reload.

      </p>

      <button

        type="button"

        disabled={insightsReevaluateLoading || !screeningId}

        title="Rebuild candidate list from the database (bypasses short-lived cache) so all charts update"

        onClick={() => refreshScreeningInsights()}

        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border-2 transition-colors whitespace-nowrap shrink-0 ${

          insightsReevaluateLoading || !screeningId

            ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'

            : 'border-[#007BFF] text-[#007BFF] bg-white hover:bg-[#007BFF]/5'

        }`}

      >

        {insightsReevaluateLoading ? (

          <>

            <span className="inline-block w-4 h-4 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />

            Refreshing…

          </>

        ) : (

          <>

            <HeroIcons.ArrowPathIcon className="h-4 w-4" />

            Reevaluate insights

          </>

        )}

      </button>

    </div>

    {/* Section 1 – Candidate overview */}

    <section className="space-y-4">

      <div>

        <h2 className="text-lg font-semibold text-gray-900">Candidate overview</h2>

        <p className="text-sm text-gray-500">

          Applied, completed, selected and assessment stats

        </p>

      </div>

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">

        {kpiCards.slice(0, 4).map((k) => renderKpiCard(k))}

      </div>

      {/* Pipeline flow – area chart + completion & selection rates (3 in 1) */}

      {invitedCount > 0 && (

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          <h3 className="text-base font-semibold text-gray-900 mb-1">Pipeline flow</h3>

          <p className="text-sm text-gray-500 mb-2">

            How candidates move through stages — at a glance

          </p>

          <p className="text-sm text-gray-600 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">

            <span>

              <span className="font-medium text-[#007BFF]">

                Completion {Math.round((completedCount / invitedCount) * 100)}%

              </span>

              <span className="text-gray-500">

                {' '}

                — {completedCount} of {invitedCount} completed

              </span>

            </span>

            <span className="text-gray-300">·</span>

            <span>

              <span className="font-medium text-emerald-600">

                Selected{' '}

                {completedCount > 0 ? Math.round((shortlistedCount / completedCount) * 100) : 0}

                %

              </span>

              <span className="text-gray-500">

                {' '}

                — {shortlistedCount} of {completedCount} selected

              </span>

            </span>

          </p>

          {s.funnelData.length > 0 ? (

            <div className="h-56">

              <ResponsiveContainer width="100%" height="100%">

                <AreaChart

                  data={s.funnelData}

                  margin={{ top: 8, right: 24, left: 0, bottom: 0 }}

                >

                  <defs>

                    <linearGradient id="poolOverviewArea" x1="0" y1="0" x2="0" y2="1">

                      <stop offset="0%" stopColor="#007BFF" stopOpacity={0.4} />

                      <stop offset="100%" stopColor="#007BFF" stopOpacity={0.05} />

                    </linearGradient>

                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

                  <XAxis dataKey="stage" tick={{ fontSize: 11 }} stroke="#6b7280" />

                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />

                  <Tooltip

                    contentStyle={{

                      borderRadius: '8px',

                      border: '1px solid #e5e7eb',

                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                    }}

                    formatter={(value, name, props) => {

                      const conv =

                        props.payload.conversion != null && props.payload.stage !== 'Applied'

                          ? ` (${props.payload.conversion}% conversion)`

                          : '';

                      return [`${value} candidates${conv}`, props.payload.stage];

                    }}

                    labelFormatter={(label) => label}

                  />

                  <Area

                    type="monotone"

                    dataKey="count"

                    name="Candidates"

                    stroke="#007BFF"

                    strokeWidth={2}

                    fill="url(#poolOverviewArea)"

                  />

                </AreaChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <div className="h-32 flex items-center justify-center text-gray-500 text-sm rounded-lg bg-gray-50/80">

              Pipeline stages will appear as candidates move through the process.

            </div>

          )}

        </div>

      )}

    </section>



    {/* Section 2 – Candidate pool details */}

    <section className="space-y-4">

      <div>

        <h2 className="text-lg font-semibold text-gray-900">Candidate pool details</h2>

        <p className="text-sm text-gray-500">

          Years of experience, education level, top skills and gender in your search pool

        </p>

      </div>



      {/* 1) Years of experience + Average tenure stats + distribution */}

      <div className="grid gap-6 lg:grid-cols-3">

        <div className="space-y-4">

          {candidatePoolInsights.experienceStats && (

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col justify-center">

              <h3 className="text-base font-semibold text-gray-900 mb-1">

                Years of Experience

              </h3>

              <p className="text-sm text-gray-500 mb-4">Total full-time work experience</p>

              <div className="flex flex-wrap items-baseline gap-6">

                <div>

                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">

                    Average

                  </p>

                  <p className="text-2xl font-bold text-[#007BFF]">

                    {candidatePoolInsights.experienceStats.avg} years

                  </p>

                </div>

                <div>

                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">

                    Median

                  </p>

                  <p className="text-2xl font-bold text-[#007BFF]">

                    {candidatePoolInsights.experienceStats.median} years

                  </p>

                </div>

              </div>

              <p className="text-xs text-gray-500 mt-4">

                {candidatePoolInsights.experienceStats.count} candidates with experience data

              </p>

            </div>

          )}

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col justify-center">

            <h3 className="text-base font-semibold text-gray-900 mb-1">Average Tenure</h3>

            <p className="text-sm text-gray-500 mb-4">

              Average time before switching companies

            </p>

            {candidatePoolInsights.tenureStats ? (

              <>

                <div className="flex flex-wrap items-baseline gap-6">

                  <div>

                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">

                      Average

                    </p>

                    <p className="text-2xl font-bold text-[#007BFF]">

                      {candidatePoolInsights.tenureStats.avg} years

                    </p>

                  </div>

                  <div>

                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">

                      Median

                    </p>

                    <p className="text-2xl font-bold text-[#007BFF]">

                      {candidatePoolInsights.tenureStats.median} years

                    </p>

                  </div>

                </div>

                <p className="text-xs text-gray-500 mt-4">

                  {candidatePoolInsights.tenureStats.count} candidates with company history

                </p>

              </>

            ) : (

              <p className="text-sm text-gray-500 py-2">

                No tenure data yet. Tenure is computed from experience years and previous

                companies — add company history to candidate profiles to see average time before

                switching.

              </p>

            )}

          </div>

        </div>

        {candidatePoolInsights.experienceDistribution &&

          candidatePoolInsights.experienceDistribution.some((d) => d.count > 0) && (

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">

              <h3 className="text-base font-semibold text-gray-900 mb-1">

                Number of Profiles by Experience

              </h3>

              <p className="text-sm text-gray-500 mb-4">

                Distribution of years of experience in your search pool

              </p>

              <div className="h-72">

                <ResponsiveContainer width="100%" height="100%">

                  <AreaChart

                    data={candidatePoolInsights.experienceDistribution}

                    margin={{ top: 8, right: 24, left: 0, bottom: 0 }}

                  >

                    <defs>

                      <linearGradient id="expDistArea" x1="0" y1="0" x2="0" y2="1">

                        <stop offset="0%" stopColor="#007BFF" stopOpacity={0.35} />

                        <stop offset="100%" stopColor="#007BFF" stopOpacity={0.05} />

                      </linearGradient>

                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

                    <XAxis dataKey="years" tick={{ fontSize: 11 }} stroke="#6b7280" />

                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />

                    <Tooltip

                      contentStyle={{

                        borderRadius: '8px',

                        border: '1px solid #e5e7eb',

                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                      }}

                      formatter={(value) => [`${value} profiles`, 'Count']}

                    />

                    <Area

                      type="monotone"

                      dataKey="count"

                      name="Profiles"

                      stroke="#007BFF"

                      strokeWidth={2}

                      fill="url(#expDistArea)"

                    />

                  </AreaChart>

                </ResponsiveContainer>

              </div>

            </div>

          )}

      </div>



      {/* 2) Gender + Education funnel side by side */}

      <div className="grid gap-6 lg:grid-cols-2">

        {/* Gender – circular chart */}

        {candidatePoolInsights.genderPie.length > 0 ? (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

            <h3 className="text-base font-semibold text-gray-900 mb-1">Gender</h3>

            <p className="text-sm text-gray-500 mb-4">

              Gender distribution in your search pool

            </p>

            <div className="h-64 flex items-center justify-center">

              <ResponsiveContainer width="100%" height="100%">

                <PieChart>

                  <Pie

                    data={candidatePoolInsights.genderPie}

                    cx="50%"

                    cy="50%"

                    innerRadius={55}

                    outerRadius={85}

                    paddingAngle={2}

                    dataKey="value"

                  >

                    {candidatePoolInsights.genderPie.map((entry, i) => (

                      <Cell key={i} fill={entry.fill} stroke="#fff" strokeWidth={2} />

                    ))}

                  </Pie>

                  <Tooltip

                    formatter={(value, name) => [

                      `${value} (${candidatePoolInsights.total > 0 ? Math.round((value / candidatePoolInsights.total) * 100) : 0}%)`,

                      name,

                    ]}

                    contentStyle={{

                      borderRadius: '8px',

                      border: '1px solid #e5e7eb',

                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                    }}

                  />

                  <Legend />

                </PieChart>

              </ResponsiveContainer>

            </div>

          </div>

        ) : (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex items-center justify-center min-h-[200px]">

            <p className="text-sm text-gray-500">No gender data in pool yet.</p>

          </div>

        )}



        {/* Education level – left-to-right funnel (wide left, tapers right, Metabase-style) */}

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          <h3 className="text-base font-semibold text-gray-900 mb-1">

            Minimum Education Level

          </h3>

          <p className="text-sm text-gray-500 mb-4">Degrees attained in your search pool</p>

          {(() => {

            const funnel = [...candidatePoolInsights.educationLevelFunnel].sort(

              (a, b) => b.count - a.count

            );

            const maxCount = Math.max(1, ...funnel.map((r) => r.count));

            const total = funnel.reduce((s, r) => s + r.count, 0) || 1;

            const w = 360;

            const h = 80;

            const n = funnel.length;

            const segW = w / n;

            const maxH = h * 0.85;

            const minH = 8;

            const getH = (c) => Math.max(minH, (c / maxCount) * maxH);

            const segments = funnel.map((row, i) => {

              const leftH = getH(row.count);

              const rightH =

                i < n - 1 ? getH(funnel[i + 1].count) : Math.max(minH, leftH * 0.3);

              const x1 = i * segW;

              const x2 = (i + 1) * segW;

              const cy = h / 2;

              const pts = `${x1},${cy - leftH / 2} ${x1},${cy + leftH / 2} ${x2},${cy + rightH / 2} ${x2},${cy - rightH / 2}`;

              const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;

              return { pts, fill: row.fill, level: row.level, count: row.count, pct };

            });

            return (

              <div className="w-full">

                <svg

                  viewBox={`0 0 ${w} ${h}`}

                  className="w-full"

                  style={{ height: 100, display: 'block' }}

                  preserveAspectRatio="xMidYMid meet"

                >

                  <title>Education level funnel</title>

                  {segments.map((seg) => (

                    <polygon

                      key={seg.level}

                      points={seg.pts}

                      fill={seg.fill}

                      stroke="rgba(255,255,255,0.7)"

                      strokeWidth="1"

                    />

                  ))}

                </svg>

                <div className="flex w-full mt-3 text-xs">

                  {segments.map((seg) => (

                    <div

                      key={seg.level}

                      className="flex-1 text-center min-w-0 px-1"

                      style={{ flexBasis: 0 }}

                    >

                      <div className="font-semibold truncate" style={{ color: seg.fill }}>

                        {seg.level}

                      </div>

                      <div className="text-gray-600">

                        {seg.count} ({seg.pct}%)

                      </div>

                    </div>

                  ))}

                </div>

              </div>

            );

          })()}

        </div>

      </div>



      {/* 3) Industry + Skills side by side */}

      <div className="grid gap-6 lg:grid-cols-2">

        {/* Top industries */}

        {candidatePoolInsights.topIndustries.length > 0 ? (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

            <h3 className="text-base font-semibold text-gray-900 mb-1">Industries</h3>

            <p className="text-sm text-gray-500 mb-4">Industries in your candidate pool</p>

            <div className="h-80 overflow-y-auto">

              <ResponsiveContainer width="100%" height="100%">

                <BarChart

                  data={candidatePoolInsights.topIndustries}

                  layout="vertical"

                  margin={{ top: 0, right: 60, left: 0, bottom: 0 }}

                >

                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />

                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" />

                  <YAxis

                    type="category"

                    dataKey="name"

                    tick={{ fontSize: 11 }}

                    stroke="#6b7280"

                    width={120}

                  />

                  <Tooltip

                    contentStyle={{

                      borderRadius: '8px',

                      border: '1px solid #e5e7eb',

                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                    }}

                    formatter={(value, name, props) => [

                      `${value} (${props?.payload?.pct ?? 0}%)`,

                      'Candidates',

                    ]}

                  />

                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Candidates">

                    {candidatePoolInsights.topIndustries.map((entry, i) => (

                      <Cell key={i} fill={entry.fill} />

                    ))}

                  </Bar>

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>

        ) : (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex items-center justify-center min-h-[200px]">

            <p className="text-sm text-gray-500">No industry data in pool yet.</p>

          </div>

        )}



        {/* Top skills – horizontal bar chart */}

        {candidatePoolInsights.topSkills.length > 0 ? (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

            <h3 className="text-base font-semibold text-gray-900 mb-1">Most common skills</h3>

            <p className="text-sm text-gray-500 mb-4">Top skills in your candidate pool</p>

            <div className="h-80 overflow-y-auto">

              <ResponsiveContainer width="100%" height="100%">

                <BarChart

                  data={candidatePoolInsights.topSkills}

                  layout="vertical"

                  margin={{ top: 0, right: 60, left: 0, bottom: 0 }}

                >

                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />

                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" />

                  <YAxis

                    type="category"

                    dataKey="name"

                    tick={{ fontSize: 11 }}

                    stroke="#6b7280"

                    width={120}

                  />

                  <Tooltip

                    contentStyle={{

                      borderRadius: '8px',

                      border: '1px solid #e5e7eb',

                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                    }}

                    formatter={(value, name, props) => [

                      `${value} (${props.payload.pct}%)`,

                      'Candidates',

                    ]}

                  />

                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Candidates">

                    {candidatePoolInsights.topSkills.map((entry, i) => (

                      <Cell key={i} fill={entry.fill} />

                    ))}

                  </Bar>

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>

        ) : (

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex items-center justify-center min-h-[200px]">

            <p className="text-sm text-gray-500">No skills data in pool yet.</p>

          </div>

        )}

      </div>



      {candidatePoolInsights.total === 0 && (

        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">

          <p className="text-sm text-gray-500">

            No candidate pool data yet. Data will appear as candidates are added.

          </p>

        </div>

      )}

    </section>

    {/* Section 3 – Score analytics: Overall first, then Resume, then Assessment/AI Interview if chosen */}

    <section className="space-y-4">

      <div>

        <h2 className="text-lg font-semibold text-gray-900">Score analytics</h2>

        <p className="text-sm text-gray-500">

          Overall and resume scores and insights

        </p>

      </div>

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">

        {[

          kpiCards.find((k) => k.key === 'avgOverall'),

          kpiCards.find((k) => k.key === 'avgResume'),

          ...(screeningMeta?.includeAssessment ? [kpiCards.find((k) => k.key === 'avgAssessment')] : []),

          ...(screeningMeta?.includeInterview ? [kpiCards.find((k) => k.key === 'avgInterview')] : []),

        ]

          .filter(Boolean)

          .map((k) => renderKpiCard(k))}

      </div>



      <div className="grid gap-6 lg:grid-cols-2">

        {/* Graph 1: Overall Score Distribution – single bar chart (theme #007BFF) */}

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          <h3 className="text-base font-semibold text-gray-900 mb-1">Overall Score Distribution</h3>

          <p className="text-sm text-gray-500 mb-4">

            Number of candidates in each score range

          </p>

          {s.scoreBuckets.some((b) => b.count > 0) ? (

            <div className="h-64">

              <ResponsiveContainer width="100%" height="100%">

                <BarChart

                  data={s.scoreBuckets}

                  margin={{ top: 8, right: 24, left: 0, bottom: 0 }}

                >

                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

                  <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />

                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" allowDecimals={false} />

                  <Tooltip

                    contentStyle={{

                      borderRadius: '8px',

                      border: '1px solid #e5e7eb',

                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',

                    }}

                    formatter={(value) => [`${value} candidates`, 'Count']}

                  />

                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={32}>

                    {s.scoreBuckets.map((_, i) => (

                      <Cell key={i} fill="#007BFF" />

                    ))}

                  </Bar>

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <div className="h-48 flex items-center justify-center text-gray-400 text-sm rounded-lg bg-gray-50/50">

              No score data yet. Complete assessments to see distribution.

            </div>

          )}

        </div>



        {/* Graph 2: Criteria match distribution (V2) or Avg Score by Parameter (V1.5) */}

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          {s.hasV2Data && s.criteriaMatchDistribution.length > 0 ? (

            <>

              <h3 className="text-base font-semibold text-gray-900 mb-1">Criteria Match Distribution</h3>

              <p className="text-sm text-gray-500 mb-4">

                How candidates match each scoring criterion ({s.v2CandidateCount} candidate{s.v2CandidateCount !== 1 ? 's' : ''})

                {s.dealBreakerCount > 0 && (

                  <span className="ml-2 text-red-600 font-medium">

                    &bull; {s.dealBreakerCount} with deal-breaker concerns

                  </span>

                )}

              </p>

              <div className="h-64">

                <ResponsiveContainer width="100%" height="100%">

                  <BarChart

                    layout="vertical"

                    data={s.criteriaMatchDistribution.map((d) => ({

                      ...d,

                      text: d.text.length > 35 ? d.text.slice(0, 35) + '…' : d.text,

                    }))}

                    margin={{ top: 4, right: 24, left: 0, bottom: 4 }}

                  >

                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />

                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />

                    <YAxis type="category" dataKey="text" width={180} tick={{ fontSize: 10 }} stroke="#9ca3af" />

                    <Tooltip

                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}

                    />

                    <Bar dataKey="strong" stackId="a" fill="#10b981" name="Strong" barSize={20} />

                    <Bar dataKey="partial" stackId="a" fill="#f59e0b" name="Partial" barSize={20} />

                    <Bar dataKey="weak" stackId="a" fill="#f97316" name="Weak" barSize={20} />

                    <Bar dataKey="not_found" stackId="a" fill="#d1d5db" name="Not Found" barSize={20} radius={[0, 4, 4, 0]} />

                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </>

          ) : (

            <>

              <h3 className="text-base font-semibold text-gray-900 mb-1">Avg Score by Parameter</h3>

              <p className="text-sm text-gray-500 mb-4">

                Average resume score per dimension (out of 10)

              </p>

              {s.resumeBreakdownData.some((d) => d.avg != null && d.count > 0) ? (

                <div className="h-64">

                  <ResponsiveContainer width="100%" height="100%">

                    <BarChart

                      layout="vertical"

                      data={s.resumeBreakdownData.filter((d) => d.avg != null && d.count > 0)}

                      margin={{ top: 4, right: 24, left: 0, bottom: 4 }}

                    >

                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />

                      <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11 }} stroke="#9ca3af" />

                      <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} stroke="#9ca3af" />

                      <Tooltip

                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}

                        formatter={(value) => [`${value}/10`, 'Avg']}

                        labelFormatter={(label) => label}

                      />

                      <Bar dataKey="norm10" radius={[0, 4, 4, 0]} barSize={28} maxBarSize={36}>

                        {s.resumeBreakdownData

                          .filter((d) => d.avg != null && d.count > 0)

                          .map((_, i) => (

                            <Cell key={i} fill="#007BFF" />

                          ))}

                      </Bar>

                    </BarChart>

                  </ResponsiveContainer>

                </div>

              ) : (

                <div className="h-48 flex items-center justify-center text-gray-400 text-sm rounded-lg bg-gray-50/50">

                  No parameter scores yet. Scores will appear after resumes are scored.

                </div>

              )}

            </>

          )}

        </div>

      </div>

    </section>



    {/* Insights Detail Modal */}

    <AnimatedModal

      isOpen={!!insightsModalCard}

      onClose={() => setInsightsModalCard(null)}

      contentClassName="bg-white rounded-xl shadow-xl sm:max-w-2xl sm:w-full max-h-[85vh] overflow-hidden"

      zIndex={100}

    >

      <div className="flex flex-col h-full max-h-[85vh]">

        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0 bg-gray-50/80">

          <h3 className="text-lg font-semibold text-gray-900">

            {modalConfig?.title}

            {insightsModalCard !== 'questions' && (

              <span className="text-gray-500 font-normal ml-1">

                ({modalCandidates.length} candidate{modalCandidates.length !== 1 ? 's' : ''})

              </span>

            )}

          </h3>

          <button

            type="button"

            onClick={() => setInsightsModalCard(null)}

            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200"

            aria-label="Close"

          >

            <HeroIcons.XMarkIcon className="h-5 w-5" />

          </button>

        </div>



        <div

          className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 min-h-0"

          style={{

            scrollbarWidth: 'thin',

            scrollbarColor: '#cbd5e1 transparent',

          }}

        >

          {insightsModalCard === 'questions' ? (

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">

              <div className="flex border-b border-gray-200 bg-gray-50/80">

                <button

                  type="button"

                  onClick={() => setInsightsModalQuestionsSubTab('assessment')}

                  className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${insightsModalQuestionsSubTab === 'assessment'

                    ? 'border-[#007BFF] text-[#007BFF] bg-white'

                    : 'border-transparent text-gray-500 hover:text-gray-700'

                    }`}

                >

                  Assessment questions

                </button>

                <button

                  type="button"

                  onClick={() => setInsightsModalQuestionsSubTab('ai')}

                  className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${insightsModalQuestionsSubTab === 'ai'

                    ? 'border-[#007BFF] text-[#007BFF] bg-white'

                    : 'border-transparent text-gray-500 hover:text-gray-700'

                    }`}

                >

                  AI questions

                </button>

              </div>

              <div className="p-4 min-h-[200px]">

                {insightsModalQuestionsSubTab === 'assessment' ? (

                  insightsModalQuestionsLoading.assessment ? (

                    <div className="flex items-center justify-center py-12">

                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#007BFF]" />

                    </div>

                  ) : insightsModalAssessmentQuestions.length === 0 ? (

                    <p className="text-gray-500 text-sm py-8 text-center">

                      No assessment attached or no questions found.

                    </p>

                  ) : (

                    <ol className="space-y-3">

                      {insightsModalAssessmentQuestions.map((q) => (

                        <li

                          key={q.num}

                          className="flex gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-3 hover:bg-gray-50"

                        >

                          <span className="flex-shrink-0 w-7 h-7 rounded-md bg-[#007BFF] text-white flex items-center justify-center text-xs font-bold">

                            {q.num}

                          </span>

                          <div className="min-w-0 flex-1">

                            {q.title && (

                              <p className="font-medium text-gray-900 text-sm">{q.title}</p>

                            )}

                            {q.content && q.content !== q.title && (

                              <p className="text-sm text-gray-600 mt-0.5">{q.content}</p>

                            )}

                          </div>

                        </li>

                      ))}

                    </ol>

                  )

                ) : insightsModalQuestionsLoading.interview ? (

                  <div className="flex items-center justify-center py-12">

                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#007BFF]" />

                  </div>

                ) : insightsModalInterviewQuestions.length === 0 ? (

                  <p className="text-gray-500 text-sm py-8 text-center">

                    No AI interview attached or no questions found.

                  </p>

                ) : (

                  <ol className="space-y-3">

                    {insightsModalInterviewQuestions.map((q) => (

                      <li

                        key={q.num}

                        className="flex gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-3 hover:bg-gray-50"

                      >

                        <span className="flex-shrink-0 w-7 h-7 rounded-md bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">

                          {q.num}

                        </span>

                        <div className="min-w-0 flex-1">

                          <p className="text-sm text-gray-900">{q.prompt || 'Question'}</p>

                          {q.type && (

                            <span className="inline-block mt-1 text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">

                              {q.type}

                            </span>

                          )}

                        </div>

                      </li>

                    ))}

                  </ol>

                )}

              </div>

            </div>

          ) : modalCandidates.length === 0 ? (

            <p className="text-gray-500 text-sm py-8 text-center">

              No candidates in this list.

            </p>

          ) : (

            <ul className="space-y-3">

              {modalCandidates.map((c, idx) => {

                const overall = getOverallScoreNumber(c);

                const assessmentPct =

                  c.assessmentScore != null &&

                    c.assessmentTotalPoints &&

                    c.assessmentTotalPoints > 0

                    ? Math.round((c.assessmentScore / c.assessmentTotalPoints) * 100)

                    : c.assessmentScore;

                return (

                  <li

                    key={c.id || c._id}

                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"

                  >

                    <div className="flex flex-wrap items-center justify-between gap-2">

                      <div className="min-w-0 flex-1">

                        <div className="flex items-center gap-2 flex-wrap">

                          <span className="text-xs font-medium text-gray-400 w-6">

                            {idx + 1}

                          </span>

                          <span

                            className="font-semibold text-gray-900 truncate block min-w-0 max-w-full"

                            title={resolveCandidateDisplayName(c) || '—'}

                          >

                            {resolveCandidateDisplayName(c) || '—'}

                          </span>

                          <span

                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${c.shortlisted

                              ? 'bg-amber-50 text-amber-700'

                              : c.status === 'Completed'

                                ? 'bg-emerald-50 text-emerald-700'

                                : c.status === 'In Progress'

                                  ? 'bg-orange-50 text-orange-700'

                                  : 'bg-slate-100 text-slate-600'

                              }`}

                            title={

                              c.shortlisted

                                ? c.shortlistedAt

                                  ? `Selected on ${new Date(c.shortlistedAt).toLocaleString('en-US', {

                                    year: 'numeric',

                                    month: 'short',

                                    day: 'numeric',

                                    hour: '2-digit',

                                    minute: '2-digit',

                                  })}`

                                  : undefined

                                : c.status === 'Completed'

                                  ? c.completedAt

                                    ? `Completed on ${new Date(c.completedAt).toLocaleString('en-US', {

                                      year: 'numeric',

                                      month: 'short',

                                      day: 'numeric',

                                      hour: '2-digit',

                                      minute: '2-digit',

                                    })}`

                                    : undefined

                                  : c.status === 'In Progress'

                                    ? c.screeningStartedAt

                                      ? `Started on ${new Date(c.screeningStartedAt).toLocaleString('en-US', {

                                        year: 'numeric',

                                        month: 'short',

                                        day: 'numeric',

                                        hour: '2-digit',

                                        minute: '2-digit',

                                      })}`

                                      : undefined

                                    : c.invitedAt

                                      ? `Invited on ${new Date(c.invitedAt).toLocaleString('en-US', {

                                        year: 'numeric',

                                        month: 'short',

                                        day: 'numeric',

                                        hour: '2-digit',

                                        minute: '2-digit',

                                      })}`

                                      : undefined

                            }

                          >

                            {c.shortlisted ? 'Selected' : (c.status || '—')}

                          </span>

                        </div>

                        <p

                          className="text-sm text-gray-500 truncate mt-0.5 ml-8"

                          title={c.email || '—'}

                        >

                          {c.email || '—'}

                        </p>

                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">

                        {c.shortlisted && (

                          <span className="text-amber-600" title="Selected">

                            <HeroIcons.StarIcon className="h-4 w-4" />

                          </span>

                        )}

                        <button

                          type="button"

                          onClick={() => {

                            setInsightsModalCard(null);

                            props.openCandidateResumeBreakdown(c);

                          }}

                          className="text-xs font-medium text-[#007BFF] hover:underline"

                        >

                          View details

                        </button>

                      </div>

                    </div>

                    <div className="flex flex-wrap gap-2 mt-3 ml-8">

                      {c.resumeScore != null ? (

                        <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium bg-blue-50 text-[#007BFF] cursor-default">

                          Resume {c.resumeScore}%

                        </span>

                      ) : c.source === 'screening_upload' &&

                        !(c.resumeUrl || c.resume?.url) &&

                        // Only show "Resume scoring…" for ZIP resume bulk uploads where a resume exists

                        // but is still being processed in the background (list/Excel uploads have no resume).

                        c.structuredResumeText == null ? (

                        <span

                          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50"

                          title="Resume is being scored in the background"

                        >

                          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />

                          Resume scoring…

                        </span>

                      ) : (

                        <span className="inline-flex items-center rounded-md px-2 py-1 text-xs text-gray-400 bg-gray-50">

                          Resume —

                        </span>

                      )}

                      {screeningMeta?.includeAssessment &&

                        (c.assessmentScore != null ? (

                          <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-violet-50 text-violet-700 cursor-default">

                            Assessment{' '}

                            {assessmentPct != null ? `${assessmentPct}%` : c.assessmentScore}

                          </span>

                        ) : (

                          <span className="inline-flex items-center rounded-md px-2 py-1 text-xs text-gray-400 bg-gray-50">

                            Assessment —

                          </span>

                        ))}

                      {screeningMeta?.includeInterview &&

                        (c.interviewScore != null || c.interviewSessionId ? (

                          <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 cursor-default">

                            Interview {c.interviewScore != null ? `${c.interviewScore}%` : '—'}

                          </span>

                        ) : (

                          <span className="inline-flex items-center rounded-md px-2 py-1 text-xs text-gray-400 bg-gray-50">

                            Interview —

                          </span>

                        ))}

                      <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 cursor-default">

                        Overall {overall != null ? `${overall}%` : '—'}

                      </span>

                    </div>

                  </li>

                );

              })}

            </ul>

          )}

        </div>

      </div>

    </AnimatedModal>

  </div>

);
}
